import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { organizationService, fetchGeo } from '@/services/api';
import { formatCurrency as formatCurrencyUtil, type Currency } from '@/utils/currency';

/**
 * Hook to get organization currency and format helper
 *
 * @param organizationCurrency - Optional currency to use directly (for public pages)
 */
export function useCurrency(organizationCurrency?: Currency) {
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
    enabled: !organizationCurrency, // Only fetch if currency not provided
  });

  const currency = organizationCurrency || (organization?.currency as Currency) || 'USD';

  const formatCurrency = (amount: number) => {
    return formatCurrencyUtil(amount, currency);
  };

  return {
    currency,
    formatCurrency,
  };
}

/**
 * Detect user's likely currency based on browser locale/timezone
 */
function detectCurrencyFromLocale(): Currency {
  try {
    // Try to get timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Brazil timezones
    if (timezone?.startsWith('America/Sao_Paulo') ||
      timezone?.startsWith('America/Fortaleza') ||
      timezone?.startsWith('America/Recife') ||
      timezone?.startsWith('America/Bahia') ||
      timezone?.startsWith('America/Belem') ||
      timezone?.startsWith('America/Manaus') ||
      timezone?.startsWith('America/Cuiaba') ||
      timezone?.startsWith('America/Porto_Velho') ||
      timezone?.startsWith('America/Rio_Branco')) {
      return 'BRL';
    }

    // Poland timezone → PLN
    if (timezone?.startsWith('Europe/Warsaw')) {
      return 'PLN';
    }

    // European timezones (Eurozone)
    const euroTimezones = [
      'Europe/Amsterdam', 'Europe/Andorra', 'Europe/Athens', 'Europe/Berlin',
      'Europe/Bratislava', 'Europe/Brussels', 'Europe/Budapest', 'Europe/Dublin',
      'Europe/Helsinki', 'Europe/Lisbon', 'Europe/Ljubljana', 'Europe/Luxembourg',
      'Europe/Madrid', 'Europe/Malta', 'Europe/Monaco', 'Europe/Paris',
      'Europe/Prague', 'Europe/Riga', 'Europe/Rome', 'Europe/San_Marino',
      'Europe/Sarajevo', 'Europe/Skopje', 'Europe/Sofia', 'Europe/Stockholm',
      'Europe/Tallinn', 'Europe/Tirane', 'Europe/Vaduz', 'Europe/Vatican',
      'Europe/Vienna', 'Europe/Vilnius', 'Europe/Zagreb',
    ];

    if (euroTimezones.some(tz => timezone?.startsWith(tz))) {
      return 'EUR';
    }

    // Fallback: check browser language
    const lang = navigator.language || 'en';

    if (lang.startsWith('pt-BR') || lang === 'pt') {
      return 'BRL';
    }

    if (lang.startsWith('pl')) {
      return 'PLN';
    }

    if (lang.startsWith('pt-PT') || lang.startsWith('de') || lang.startsWith('fr') || lang.startsWith('es') ||
      lang.startsWith('it') || lang.startsWith('nl')) {
      return 'EUR';
    }

  } catch {
    // Ignore detection errors
  }

  // Default to USD
  return 'USD';
}

/**
 * Hook that detects the user's currency via the backend geo endpoint (IP-based).
 * Falls back to client-side locale/timezone detection if the API call fails.
 *
 * Returns { currency, country, isLoading, formatPrice, getStartingPrice }.
 */
export function useDetectedCurrency() {
  const localeFallback = useMemo(() => detectCurrencyFromLocale(), []);

  const { data, isLoading } = useQuery({
    queryKey: ['geo'],
    queryFn: fetchGeo,
    staleTime: 1000 * 60 * 60, // 1 hour – geo doesn't change often
    gcTime: 1000 * 60 * 60 * 24, // keep in cache for 24h
    retry: 1,
  });

  const currency: Currency = data?.currency
    ? (data.currency.toUpperCase() as Currency)
    : localeFallback;

  const formatPrice = (amount: number) => formatCurrencyUtil(amount, currency);

  // Lowest-tier starting price per currency (used only for marketing copy interpolation).
  // Keep in sync with Stripe basic plan prices.
  const STARTING_PRICES: Record<Currency, number> = { USD: 30, EUR: 25, BRL: 99, PLN: 99 };
  const getStartingPrice = () => formatPrice(STARTING_PRICES[currency]);

  return {
    currency,
    country: data?.country ?? null,
    isLoading,
    formatPrice,
    getStartingPrice,
  };
}
